import { makeObservable, observable, action, runInAction } from "mobx";
import {
  WithBooleanFlag,
  ExpandableKeys,
  PerPageSettings,
  StatusInfo,
} from "./core";
import { webAPI } from "../api/Api";
import Constants from "../constants/Common";
import { Dictionary } from "../interface/common";
import { FilterCondition } from "../constants/Enums";

class SmsSettingsStore {
  incidentsParamsAreLoading = new WithBooleanFlag();
  incidentsParamsData = [];
  incidentsParamsDataPaginator = new PerPageSettings();
  incidentParamsRowKeys = new ExpandableKeys();
  incidentParamsStatus = new StatusInfo();
  incidentParamsFormIsSending = new WithBooleanFlag(false);

  distributionListsAreLoading = new WithBooleanFlag();
  distributionListsData = [];
  distributionListDataPaginator = new PerPageSettings();
  distributionListRowKeys = new ExpandableKeys();
  distributionListStatus = new StatusInfo();
  distributionListFormIsSending = new WithBooleanFlag(false);
  templatesAreLoading = new WithBooleanFlag();
  templatesData = [];
  templatesDataPaginator = new PerPageSettings();
  templatesRowKeys = new ExpandableKeys();
  templateStatus = new StatusInfo();
  templatesFormIsSending = new WithBooleanFlag(false);
  templatesTextExample: Dictionary<string> = {};
  logLoading = new WithBooleanFlag();
  logData = [];
  logDataPaginator = new PerPageSettings();
  logRowKeys = new ExpandableKeys();

  constructor() {
    makeObservable(
      this,
      {
        incidentsParamsData: observable,
        distributionListsData: observable,
        templatesData: observable,
        logData: observable,
        getSmsIncidentsParams: action,
        getSmsDistributionsLists: action,
        getSmsTemplates: action,
        saveSmsIncidentParams: action,
        saveSmsDistributionList: action,
        saveSmsTemplate: action,
        deleteSmsIncidentsParam: action,
        deleteSmsDistributionList: action,
        deleteSmsTemplate: action,
        // getTemplatesTextExample: action,
        // templatesTextExample: observable,
      },
      {
        deep: true,
      },
    );
  }

  async getSmsIncidentsParams(forceUpdate = true) {
    if (!forceUpdate && this.incidentsParamsData.length) {
      return;
    }

    this.incidentsParamsAreLoading.setTrue();

    try {
      const res = await webAPI.post(`/sp/NotificationEvent/EventSeverities`, {
        page: this.incidentsParamsDataPaginator.page,
        count: this.incidentsParamsDataPaginator.perPage,
      });
      runInAction(() => {
        this.incidentsParamsData = res.data.items;
        this.incidentsParamsDataPaginator.updateMeta(res.data);
      });
    } catch (e) {
      console.log(e);
    } finally {
      this.incidentsParamsAreLoading.setFalse();
    }
  }

  async findSmsIncidentsParamsById(page: number, count: number, value: number) {
    try {
      const res = await webAPI.post(`/sp/NotificationEvent/EventSeverities`, {
        page,
        count,
        filterItems: value
          ? [{ fieldName: "id", value, condition: FilterCondition.Equal }]
          : null,
      });
      return res;
    } catch (e) {
      console.log(e);
    }
  }

  async findSmsIncidentsParamsByName(page: number, count: number, value: string) {
    try {
      const res = await webAPI.post(`/sp/NotificationEvent/EventSeverities`, {
        page,
        count,
        filterItems: value
          ? [{ fieldName: "name", value, condition: FilterCondition.Like }]
          : null,
      });
      return res;
    } catch (e) {
      console.log(e);
    }
  }

  async saveSmsIncidentParams(id = 0, values) {
    this.incidentParamsFormIsSending.setTrue();
    this.incidentParamsStatus.resetStatus();

    try {
      const res = !id
        ? await webAPI.post(`/sp/NotificationEvent/EventSeverity`, values)
        : await webAPI.put(`/sp/NotificationEvent/EventSeverity/${id}`, values);

      runInAction(() => {
        !id
          ? this.incidentParamsStatus.setStatus(
              Constants.requestStatuses.created,
            )
          : this.incidentParamsStatus.setStatus(
              Constants.requestStatuses.updated,
            );
        this.getSmsIncidentsParams();
        this.incidentParamsRowKeys.remove(id);
      });
    } catch (e) {
      this.incidentParamsStatus.setDetails(e.response.data?.errors);
      this.incidentParamsStatus.setStatus(Constants.requestStatuses.error);
    } finally {
      this.incidentParamsFormIsSending.setFalse();
    }
  }

  async getSmsDistributionsLists() {
    this.distributionListsAreLoading.setTrue();

    try {
      const res = await webAPI.post(`/sp/NotificationEvent/ClientGroups`, {
        page: this.distributionListDataPaginator.page,
        count: this.distributionListDataPaginator.perPage,
      });
      runInAction(() => {
        this.distributionListsData = res.data.items;
        this.distributionListDataPaginator.updateMeta(res.data);
      });
    } catch (e) {
      console.log(e);
    } finally {
      this.distributionListsAreLoading.setFalse();
    }
  }

  async saveSmsDistributionList(id = 0, values) {
    this.distributionListFormIsSending.setTrue();
    this.distributionListStatus.resetStatus();

    try {
      const res = !id
        ? await webAPI.post(`/sp/NotificationEvent/ClientGroup`, values)
        : await webAPI.put(`/sp/NotificationEvent/ClientGroup/${id}`, values);

      runInAction(() => {
        !id
          ? this.distributionListStatus.setStatus(
              Constants.requestStatuses.created,
            )
          : this.distributionListStatus.setStatus(
              Constants.requestStatuses.updated,
            );
        this.getSmsDistributionsLists();
        this.distributionListRowKeys.remove(id);
      });
    } catch (e) {
      this.distributionListStatus.setDetails(e.response.data?.errors);
      this.distributionListStatus.setStatus(Constants.requestStatuses.error);
    } finally {
      this.distributionListFormIsSending.setFalse();
    }
  }

  async getSmsTemplates() {
    this.templatesAreLoading.setTrue();

    try {
      const res = await webAPI.post(`/sp/NotificationEvent/Templates`, {
        page: this.templatesDataPaginator.page,
        count: this.templatesDataPaginator.perPage,
      });
      runInAction(() => {
        this.templatesData = res.data.items;
        this.templatesDataPaginator.updateMeta(res.data);
      });
    } catch (e) {
      console.log(e);
    } finally {
      this.templatesAreLoading.setFalse();
    }
  }

  async getLog() {
    this.templatesAreLoading.setTrue();

    try {
      const res = await webAPI.post(`/sp/NotificationEvent/Notifications`, {
        page: this.logDataPaginator.page,
        count: this.logDataPaginator.perPage,
      });
      runInAction(() => {
        this.logData = res.data.items;
        this.logDataPaginator.updateMeta(res.data);
      });
    } catch (e) {
      console.log(e);
    } finally {
      this.logLoading.setFalse();
    }
  }

  async saveSmsTemplate(id = 0, values) {
    this.templatesFormIsSending.setTrue();
    this.templateStatus.resetStatus();

    try {
      const res = !id
        ? await webAPI.post(`/sp/NotificationEvent/Template`, values)
        : await webAPI.put(`/sp/NotificationEvent/Template/${id}`, values);

      runInAction(() => {
        !id
          ? this.templateStatus.setStatus(Constants.requestStatuses.created)
          : this.templateStatus.setStatus(Constants.requestStatuses.updated);
        this.getSmsTemplates();
        this.templatesRowKeys.remove(id);
      });
    } catch (e) {
      this.templateStatus.setDetails(e.response.data?.errors);
      this.templateStatus.setStatus(Constants.requestStatuses.error);
    } finally {
      this.templatesFormIsSending.setFalse();
    }
  }

  async deleteSmsIncidentsParam(id) {
    this.incidentParamsStatus.resetStatus();

    try {
      await webAPI.delete(`/sp/NotificationEvent/EventSeverity/${id}`);

      runInAction(() => {
        this.incidentParamsStatus.setStatus(Constants.requestStatuses.deleted);
        this.getSmsIncidentsParams();
      });
    } catch (e) {
      this.incidentParamsStatus.setDetails(e.response.data?.errors);
      this.incidentParamsStatus.setStatus(Constants.requestStatuses.error);
    }
  }

  async deleteSmsDistributionList(id) {
    this.distributionListStatus.resetStatus();

    try {
      await webAPI.delete(`/sp/NotificationEvent/ClientGroup/${id}`);

      runInAction(() => {
        this.distributionListStatus.setStatus(
          Constants.requestStatuses.deleted,
        );
        this.getSmsDistributionsLists();
      });
    } catch (e) {
      this.distributionListStatus.setDetails(e.response.data?.errors);
      this.distributionListStatus.setStatus(Constants.requestStatuses.error);
    }
  }

  async deleteSmsTemplate(id) {
    this.templateStatus.resetStatus();

    try {
      await webAPI.delete(`/sp/NotificationEvent/Template/${id}`);

      runInAction(() => {
        this.templateStatus.setStatus(Constants.requestStatuses.deleted);
        this.getSmsTemplates();
      });
    } catch (e) {
      this.templateStatus.setDetails(e.response.data?.errors);
      this.templateStatus.setStatus(Constants.requestStatuses.error);
    }
  }

  // async getTemplatesTextExample(id, text) {
  //   if (!text) {
  //     this.templatesTextExample[id] = "";
  //     return;
  //   }

  //   try {
  //     const res = await webAPI.get(
  //       `/sp/NotificationEvent/NotificationData/${Constants.referenceDefectId}/Text`,
  //       { params: { text: text } },
  //     );

  //     runInAction(() => {
  //       this.templatesTextExample[id] = res.data.item1;
  //     });
  //   } catch (e) {
  //     console.log(e);
  //   }
  // }
}

const smsSettings = new SmsSettingsStore();
export default smsSettings;
